import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Builders from './pages/Builders';
import Architects from './pages/Architects';
import Homeowners from './pages/Homeowners';
import Partners from './pages/Partners';
import About from './pages/About';
import Consulting from './pages/Consulting';
import Contact from './pages/Contact';
import Mastermind from './pages/Mastermind';
import FieldPartnersWaitlist from './components/FieldPartnersWaitlist';
import './App.css';
import Wellwired from './pages/Wellwired';
import WellwiredNext from './pages/WellwiredNext';
import WellwiredReview from './pages/WellwiredReview';
import PageMetadata from './components/PageMetadata';

function ScrollToTop({ location: { pathname, hash } }) {
  useLayoutEffect(() => {
    // Position the incoming page before it appears, without moving the page
    // that is still fading out. Deep links keep their intended destination.
    const positionPage = () => {
      const section = hash && document.getElementById(hash.slice(1));
      if (section) section.scrollIntoView({ behavior: 'instant' });
      else window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };
    positionPage();
    // Recheck after the browser's initial layout and scroll restoration.
    const frame = requestAnimationFrame(positionPage);
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);
  return null;
}

function App() {
  const location = useLocation();
  const reducedMotion = useReducedMotion();
  const hasMounted = useRef(false);
  const path = location.pathname.replace(/\/+$/, '') || '/';
  const isWellwired = ['/wellwired', '/wellwired-a', '/wellwired-b', '/wellwired-review'].includes(path);
  useEffect(() => { hasMounted.current = true; }, []);

  return (
    <div className="site-transition">
      <PageMetadata />
      <AnimatePresence mode="wait">
        <motion.div
          key={isWellwired ? path : 'bai'}
          className="site-surface"
          initial={!reducedMotion && (isWellwired || hasMounted.current) ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: reducedMotion ? 0 : 0.24, ease: [0.45, 0, 0.25, 1] } }}
          transition={{ duration: reducedMotion ? 0 : 0.9, ease: [0.45, 0, 0.25, 1] }}
        >
          <ScrollToTop location={location} />
          {isWellwired ? (
            path === '/wellwired' ? <Wellwired /> :
            path === '/wellwired-review' ? <WellwiredReview /> :
            <WellwiredNext variant={path === '/wellwired-b' ? 'b' : 'a'} />
          ) : (
            <div className="app">
              <Header location={location} />
              <main>
                <AnimatePresence mode="wait">
                  <Routes location={location} key={location.pathname}>
                    <Route path="/" element={<Home />} />
                    <Route path="/builders" element={<Builders />} />
                    <Route path="/architects" element={<Architects />} />
                    <Route path="/homeowners" element={<Homeowners />} />
                    <Route path="/partners" element={<Partners />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/consulting" element={<Consulting />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/mastermind" element={<Mastermind />} />
                    <Route path="/partner-program" element={<FieldPartnersWaitlist />} />
                  </Routes>
                </AnimatePresence>
              </main>
              <Footer />
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default App;
